/**
 * Avatar upload shared by the own photo (/avatar, api/auth.ts) and another member's photo
 * (/members/:id/avatar, editors and admins, api/members.ts): 512 px then 128 px JPEG under the
 * next revision, then a commit by the caller.
 */
import type { StaleAvatarRevDetails } from '../../shared/dto/members';
import { ApiFailure, api } from './client';

async function putParts(base: string, rev: number, blob512: Blob, blob128: Blob): Promise<void> {
  await api<unknown>(`${base}/${rev}/512`, { method: 'PUT', raw: blob512, rawType: 'image/jpeg', timeoutMs: 60_000 });
  await api<unknown>(`${base}/${rev}/128`, { method: 'PUT', raw: blob128, rawType: 'image/jpeg', timeoutMs: 60_000 });
}

/** `nextRev` of a 409 stale_avatar_rev (StaleAvatarRevDetails), when the server sent one. */
export function staleAvatarNextRev(e: unknown): number | null {
  if (!(e instanceof ApiFailure && e.status === 409)) return null;
  const next = (e.details as Partial<StaleAvatarRevDetails> | null | undefined)?.nextRev;
  return typeof next === 'number' && Number.isInteger(next) && next > 0 ? next : null;
}

/**
 * Uploads both parts under `guess` (avatarRev + 1) and returns the revision to commit. Revision
 * numbers are never reused (avatar URLs are cached as immutable), so after a removal — or a change
 * on another device or by someone else — the server answers 409 with the revision it expects
 * instead; the upload is retried once with that (`fallbackNext` only if the 409 did not say).
 */
export async function putAvatarParts(
  base: '/avatar' | `/members/${string}/avatar`,
  guess: number,
  blob512: Blob,
  blob128: Blob,
  fallbackNext: () => Promise<number>,
): Promise<number> {
  let rev = guess;
  try {
    await putParts(base, rev, blob512, blob128);
  } catch (e) {
    if (!(e instanceof ApiFailure && e.status === 409)) throw e;
    rev = staleAvatarNextRev(e) ?? (await fallbackNext());
    await putParts(base, rev, blob512, blob128);
  }
  return rev;
}
